import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Alert, Input
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const currencySymbol = (raw) => {
  const u = (raw || "TRY").toString().trim().toUpperCase();
  return u === "EUR" || u === "EURO" ? "€" : "₺";
};
const fmtCur = (v, cur) => {
  if (v == null || v === "") return "-";
  const n = parseFloat(v);
  if (Number.isNaN(n)) return "-";
  return `${n.toLocaleString("tr-TR")} ${currencySymbol(cur)}`;
};


const yildizRender = (puan) => {
  const sayi = Math.round(parseFloat(puan) || 0);
  return (
    <span>
      {[1, 2, 3, 4, 5].map(i => (
        <i key={i} className={`bx ${i <= sayi ? "bxs-star text-warning" : "bx-star text-muted"}`} />
      ))}
      <span className="ms-1 text-muted small">({parseFloat(puan || 0).toFixed(1)})</span>
    </span>
  );
};

const IlanDegerlemeleri = () => {
  document.title = "İlan Değerlendirmeleri | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  const [yorumlar, setYorumlar] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [puanFilter, setPuanFilter] = useState("tumu");

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const fetchYorumlar = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${BASE_URL}/YORUMLAR?$top=500&$orderby=ID desc&$expand=ILANLAR($select=ID,AD,FIYAT,PARA_BIRIMI),KULLANICILAR($select=AD_SOYAD,Mail_Adress)`
      );
      const result = await response.json();
      if (result?.value) setYorumlar(result.value);
    } catch {
      setError("Yorumlar yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchYorumlar();
  }, []);

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      const resp = await fetch(`${BASE_URL}/YORUMLAR(${selectedItem.ID})`, {
        method: "DELETE",
        headers
      });
      if (!resp.ok) throw new Error();
      toast.success("Yorum silindi.");
      setDeleteModal(false);
      fetchYorumlar();
    } catch {
      toast.error("Silme işlemi sırasında hata oluştu.");
    }
  };

  const filteredData = useMemo(() => {
    if (puanFilter === "tumu") return yorumlar;
    const min = parseInt(puanFilter);
    return yorumlar.filter(y => Math.round(parseFloat(y.PUAN || 0)) === min);
  }, [yorumlar, puanFilter]);

  const ortalamaPuan = useMemo(() => {
    const puanlilar = yorumlar.filter(y => y.PUAN != null);
    if (!puanlilar.length) return "0.0";
    return (puanlilar.reduce((sum, y) => sum + parseFloat(y.PUAN || 0), 0) / puanlilar.length).toFixed(1);
  }, [yorumlar]);

  const puanDagilimi = useMemo(() => {
    const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    yorumlar.forEach(y => {
      const p = Math.round(parseFloat(y.PUAN || 0));
      if (dist[p] !== undefined) dist[p]++;
    });
    return dist;
  }, [yorumlar]);

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: c => <span className="fw-bold text-muted">#{c.getValue()}</span>
    },
    {
      header: "İlan",
      accessorKey: "ILANLAR",
      enableSorting: false,
      enableColumnFilter: false,
      cell: c => {
        const ilan = c.getValue();
        return ilan ? (
          <div>
            <div className="fw-medium">{ilan.AD}</div>
            {ilan.FIYAT != null && (
              <small className="text-muted">
                {fmtCur(ilan.FIYAT, ilan.PARA_BIRIMI)}
              </small>
            )}
          </div>
        ) : (
          <span className="text-muted fst-italic">İlan #{c.row.original.ILANLAR_ID}</span>
        );
      }
    },
    {
      header: "Yorumu Yapan",
      accessorKey: "KULLANICILAR",
      enableSorting: false,
      enableColumnFilter: false,
      cell: c => {
        const u = c.getValue();
        return u ? (
          <div>
            <div className="fw-medium">{u.AD_SOYAD}</div>
            <small className="text-muted">{u.Mail_Adress}</small>
          </div>
        ) : (
          <span className="text-muted fst-italic">Kullanıcı #{c.row.original.YORUM_YAPAN_ID}</span>
        );
      }
    },
    {
      header: "Puan",
      accessorKey: "PUAN",
      enableSorting: true,
      enableColumnFilter: false,
      cell: c => yildizRender(c.getValue())
    },
    {
      header: "Yorum",
      accessorKey: "YORUM_METNI",
      enableSorting: false,
      enableColumnFilter: false,
      cell: c => (
        <span style={{ maxWidth: 260, display: "block", whiteSpace: "normal", wordBreak: "break-word" }}>
          {c.getValue() || <span className="text-muted fst-italic">Yorum yok</span>}
        </span>
      )
    },
    {
      header: "Tarih",
      accessorKey: "YORUM_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: c => c.getValue()
        ? new Date(c.getValue()).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" })
        : "-"
    },
    {
      header: "İşlem",
      enableSorting: false,
      enableColumnFilter: false,
      cell: cellProps => {
        const item = cellProps.row.original;
        return (
          <Button
            color="danger"
            size="sm"
            outline
            title="Yorumu Sil"
            onClick={() => { setSelectedItem(item); setDeleteModal(true); }}
          >
            <i className="bx bx-trash" />
          </Button>
        );
      }
    }
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="İlan Değerlendirmeleri" />

          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          {/* İstatistik kartları */}
          <Row className="mb-3">
            <Col sm="12">
              <div className="d-flex gap-3 flex-wrap">
                <Card className="mini-stats-wid shadow-sm border-0 mb-0">
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bx bx-list-check fs-4 text-primary" />
                      <div>
                        <p className="text-muted mb-0 small">Toplam Yorum</p>
                        <h5 className="mb-0">{yorumlar.length}</h5>
                      </div>
                    </div>
                  </CardBody>
                </Card>
                <Card className="mini-stats-wid shadow-sm border-0 mb-0">
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bx bxs-star fs-4 text-warning" />
                      <div>
                        <p className="text-muted mb-0 small">Ortalama Puan</p>
                        <h5 className="mb-0">{ortalamaPuan} / 5</h5>
                      </div>
                    </div>
                  </CardBody>
                </Card>
                {[5, 4, 3, 2, 1].map(p => (
                  <Card
                    key={p}
                    className={`mini-stats-wid shadow-sm mb-0 ${puanFilter === String(p) ? "border-warning" : "border-0"}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setPuanFilter(puanFilter === String(p) ? "tumu" : String(p))}
                  >
                    <CardBody className="p-3">
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-warning fw-bold fs-6">{p}★</span>
                        <div>
                          <p className="text-muted mb-0 small">Yorum</p>
                          <h5 className="mb-0">{puanDagilimi[p]}</h5>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h4 className="card-title mb-0">Yorum Listesi</h4>
                      {puanFilter !== "tumu" && (
                        <small className="text-warning fw-medium">
                          Filtre: {puanFilter}★ ({filteredData.length} yorum)
                          <Button color="link" size="sm" className="p-0 ms-2 text-muted" onClick={() => setPuanFilter("tumu")}>
                            Temizle ×
                          </Button>
                        </small>
                      )}
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      <Input
                        type="select"
                        bsSize="sm"
                        value={puanFilter}
                        onChange={e => setPuanFilter(e.target.value)}
                        style={{ width: 140 }}
                      >
                        <option value="tumu">Tüm Puanlar</option>
                        <option value="5">5 Yıldız</option>
                        <option value="4">4 Yıldız</option>
                        <option value="3">3 Yıldız</option>
                        <option value="2">2 Yıldız</option>
                        <option value="1">1 Yıldız</option>
                      </Input>
                      <Button color="primary" size="sm" outline onClick={fetchYorumlar} disabled={isLoading}>
                        <i className="bx bx-refresh me-1" />
                        Yenile
                      </Button>
                    </div>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={filteredData}
                      isGlobalFilter={true}
                      SearchPlaceholder="İlan veya kullanıcı ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Silme Onay Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered size="sm">
        <ModalHeader toggle={() => setDeleteModal(false)}>Yorumu Sil</ModalHeader>
        <ModalBody>
          {selectedItem && (
            <div>
              <p className="mb-2">
                <strong>{selectedItem.KULLANICILAR?.AD_SOYAD || `Kullanıcı #${selectedItem.YORUM_YAPAN_ID}`}</strong>{" "}
                tarafından yapılan yorumu silmek istediğinize emin misiniz?
              </p>
              {selectedItem.YORUM_METNI && (
                <div className="alert alert-light border fst-italic small">
                  "{selectedItem.YORUM_METNI}"
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDeleteModal(false)}>İptal</Button>
          <Button color="danger" onClick={handleDelete}>Sil</Button>
        </ModalFooter>
      </Modal>

      <ToastContainer autoClose={2000} />
    </React.Fragment>
  );
};

export default IlanDegerlemeleri;
